import { MULTIPART } from "../../../components/Esign/signing/adopt-form.ts"
import { jsonBody } from "../../../components/Esign/signing/bytes.ts"
import { accepted, toOutcome } from "../../../components/Esign/signing/outcome.ts"
import type { Outcome } from "../../../components/Esign/signing/types.ts"
import { request } from "../../../services/api"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { saveBlob } from "../../../utils/files/save-blob.ts"
import { pickFormError } from "../../../utils/handlers/pick-form-error.ts"
import type {
  ExternalSignature,
  ExternalSignatureUpload,
  LiveSignatureRequest
} from "../types/external-signature.types.ts"

const BASE_URL = "/esign/external-signatures"

export type FileOutcome =
  | { kind: "filed"; signature: ExternalSignature }
  | { kind: "live_request"; requests: LiveSignatureRequest[] }
  | { kind: "refused"; message: string | null; errors: Record<string, string[]> | null }

const asForm = (upload: ExternalSignatureUpload) => {
  const form = new FormData()
  form.append("lead", upload.lead)
  form.append("file", upload.file)
  form.append("signed_on", upload.signedOn)
  upload.signers.forEach((name) => form.append("signers", name))
  if (upload.note) form.append("note", upload.note)
  if (upload.contract) form.append("contract", upload.contract)
  if (upload.withdrawLiveRequests) form.append("withdraw_live_requests", "true")
  return form
}

export const fileExternalSignature = async (
  upload: ExternalSignatureUpload
): Promise<FileOutcome> => {
  const response = await request.api.post(`${BASE_URL}/`, asForm(upload), {
    headers: MULTIPART
  })
  if (response.ok) {
    return { kind: "filed", signature: unwrapEnvelope<ExternalSignature>(response.data) as ExternalSignature }
  }
  const body = unwrapEnvelope<{ code?: string; live_requests?: LiveSignatureRequest[] } | null>(
    response.data
  )
  if (response.status === 409 && body?.code === "live_signature_request") {
    return { kind: "live_request", requests: body.live_requests ?? [] }
  }
  const picked = pickFormError(response.data)
  return {
    kind: "refused",
    message: typeof picked === "string" ? picked : null,
    errors: picked && typeof picked === "object" ? (picked as Record<string, string[]>) : null
  }
}

export const listExternalSignatures = async (
  leadId: string
): Promise<ExternalSignature[] | null> => {
  const response = await request.api.get(`${BASE_URL}/`, { lead: leadId, page_size: 100 })
  if (!response.ok) return null
  const page = unwrapEnvelope<{ results: ExternalSignature[] } | ExternalSignature[]>(response.data)
  return Array.isArray(page) ? page : (page?.results ?? [])
}

const save = async (path: string, filename: string): Promise<Outcome<unknown>> => {
  const response = await request.api.get<Blob>(path, {}, { responseType: "blob" })
  if (!response.ok || !response.data) {
    return toOutcome(response.status ?? 0, await jsonBody(response.data))
  }
  saveBlob(response.data, filename)
  return accepted(response.status ?? 200, null)
}

export const saveExternalDocument = (signature: ExternalSignature) =>
  save(`${BASE_URL}/${signature.id}/document/`, signature.name)

export const saveExternalRecord = (signature: ExternalSignature) =>
  save(
    `${BASE_URL}/${signature.id}/record/`,
    `${signature.name.replace(/\.[^.]+$/, "")} - Record of external signature.pdf`
  )
